import { Patient, ActivityStep } from "@/components/PatientCard";
import { formatDistanceToNow, parseISO, differenceInHours, differenceInMinutes, format } from "date-fns";

/**
 * Helper to calculate elapsed time from MD (Marked for Discharge) completion time.
 * The timer starts when the patient is marked for discharge (MD activity completed).
 * @param mdCompletionTime - The completion time of the MD activity
 * @param endTime - Optional end time (for discharged patients), defaults to current time
 */
export const calculateElapsedTime = (mdCompletionTime: string | null, endTime?: string | null): string => {
  if (!mdCompletionTime) return "00:00:00";
  
  try {
    const mdTime = new Date(mdCompletionTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end.getTime() - mdTime.getTime();
    
    // Handle negative values
    if (diffMs < 0) {
      return "00:00:00";
    }
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  } catch {
    return "00:00:00";
  }
};

// Helper to format TAT from hours to display format
export const formatTAT = (hours: number | null): string => {
  if (!hours) return "0h";
  
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/** Format time only as 12-hour with AM/PM (e.g. "03:38 PM") */
export const formatTimeAMPM = (timestamp: string | Date | null): string => {
  if (!timestamp) return "";
  try {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    if (isNaN(date.getTime())) return "";
    return format(date, "hh:mm a");
  } catch {
    return "";
  }
};

/** Format date and time with AM/PM (e.g. "29 January 2026 - 03:38 PM") */
export const formatDateTimeAMPM = (timestamp: string | Date | null): string => {
  if (!timestamp) return "";
  try {
    const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    if (isNaN(date.getTime())) return "";
    return format(date, "dd MMMM yyyy - hh:mm a");
  } catch {
    return "";
  }
};

// Helper to format activity timestamp to match screenshot format (AM/PM)
export const formatActivityTimestamp = (timestamp: string | null): string => {
  return formatDateTimeAMPM(timestamp) || (timestamp ?? "");
};


// Transform database patient record to frontend Patient type
// Note: elapsedTime is now calculated from MD completion time in components using useMDCompletionTime hook
export const transformDbPatientToFrontend = (dbPatient: any): Patient => {
  // Debug: Log database patient ID
  console.log('🔍 Transform - dbPatient.id:', dbPatient.id, 'patient_id:', dbPatient.patient_id);
  
  // Use stored elapsed_time from database, or default to "00:00:00"
  // Actual elapsed time from MD completion is calculated in components using useTotalElapsedTime + useMDCompletionTime
  const elapsedTime = dbPatient.elapsed_time || "00:00:00";
  
  return {
    name: dbPatient.name || "",
    id: dbPatient.patient_id || "",
    uuid: dbPatient.id, // Database UUID for querying activities
    age: dbPatient.age || 0,
    gender: dbPatient.gender || "M",
    doctor: dbPatient.doctor || "",
    expectedDischarge: dbPatient.expected_discharge || dbPatient.discharge_date || "",
    timer: dbPatient.timer || elapsedTime,
    categories: dbPatient.categories || ["Awaiting Discharge Initiation"],
    initiated: dbPatient.initiated || false,
    phone: dbPatient.phone || "",
    admissionNumber: dbPatient.admission_number || "",
    ward: dbPatient.ward_name || "",
    bed: dbPatient.bed || "",
    room: dbPatient.room || "",
    admissionDate: dbPatient.admission_datetime ? formatDateTimeAMPM(dbPatient.admission_datetime) : "",
    dischargeDate: dbPatient.discharge_date || "",
    diagnosis: dbPatient.diagnosis || "",
    los: dbPatient.los || "",
    dischargeStatus: dbPatient.discharge_status || "active",
    elapsedTime: elapsedTime,
    physicalDischargeCompletionTime: dbPatient.physical_discharge_completion_time ?? null,
    overallProgress: dbPatient.overall_progress || 0,
    admissionDateTime: dbPatient.admission_datetime ? new Date(dbPatient.admission_datetime) : undefined,
    activityTimeline: [], // Will be populated separately when needed
  };
};

// Transform database activity to frontend ActivityStep type
export const transformDbActivityToTimeline = (dbActivity: any): ActivityStep => {
  // Derive timeTakenMinutes for completed activities:
  // 1) Prefer explicit time_taken_minutes from DB
  // 2) Fallback to (completion_time - started_at) in minutes when available
  let derivedTimeTakenMinutes: number | undefined = undefined;
  if (dbActivity.time_taken_minutes != null && dbActivity.time_taken_minutes > 0) {
    derivedTimeTakenMinutes = dbActivity.time_taken_minutes;
  } else if (dbActivity.started_at && dbActivity.completion_time) {
    try {
      const start = new Date(dbActivity.started_at);
      const end = new Date(dbActivity.completion_time);
      const diffMs = end.getTime() - start.getTime();
      if (diffMs > 0) {
        derivedTimeTakenMinutes = Math.floor(diffMs / (1000 * 60));
      }
    } catch {
      // If parsing fails, leave derivedTimeTakenMinutes as undefined
    }
  }

  const step: ActivityStep = {
    id: dbActivity.activity_id || dbActivity.id,
    name: dbActivity.name || "",
    status: dbActivity.status || "pending",
    timestamp: dbActivity.timestamp || undefined,
    elapsedTime: dbActivity.elapsed_time || undefined,
    description: dbActivity.description || undefined,
    notes: dbActivity.notes || undefined,
    assignedAt: dbActivity.assigned_at || undefined,
    startedAt: dbActivity.started_at || undefined,
    completionTime: dbActivity.completion_time || undefined,
    timeTakenMinutes: derivedTimeTakenMinutes,
    
    // TAT tracking fields
    workflowStep: dbActivity.workflow_step || undefined,
    previousStep: dbActivity.previous_step || undefined,
    patientId: dbActivity.patient_id || undefined,
    
    // Transform completed_by staff info
    completedBy: dbActivity.completed_by_profile ? {
      id: dbActivity.completed_by_profile.id,
      fullName: dbActivity.completed_by_profile.full_name,
      email: dbActivity.completed_by_profile.email,
      phone: dbActivity.completed_by_profile.phone_number,
      role: dbActivity.completed_by_profile.role,
      department: dbActivity.completed_by_profile.department,
      designation: dbActivity.completed_by_profile.designation,
    } : undefined,
    
    // Transform assigned_to staff info
    assignedTo: dbActivity.assigned_to_profile ? {
      id: dbActivity.assigned_to_profile.id,
      fullName: dbActivity.assigned_to_profile.full_name,
      email: dbActivity.assigned_to_profile.email,
      phone: dbActivity.assigned_to_profile.phone_number,
      role: dbActivity.assigned_to_profile.role,
      department: dbActivity.assigned_to_profile.department,
      designation: dbActivity.assigned_to_profile.designation,
    } : undefined,
    
    // Transform activity history
    history: dbActivity.activity_history?.map((h: any) => ({
      id: h.id,
      actionType: h.action_type,
      timestamp: h.timestamp,
      durationMinutes: h.duration_minutes,
      notes: h.notes,
      staff: {
        id: h.staff?.id || '',
        fullName: h.staff?.full_name || '',
        email: h.staff?.email,
        phone: h.staff?.phone_number,
        role: h.staff?.role,
        department: h.staff?.department,
      }
    })) || [],
    
    // Legacy support
    doctor: dbActivity.doctor || undefined,
    staffName: dbActivity.completed_by_profile?.full_name || dbActivity.completed_by || dbActivity.staff_name || dbActivity.doctor || undefined,
  };

  // Handle parallel workflows
  if (dbActivity.is_parallel_workflow && dbActivity.parallel_paths) {
    step.isParallelWorkflow = true;
    step.parallelPaths = Array.isArray(dbActivity.parallel_paths) 
      ? dbActivity.parallel_paths.map((path: any) => ({
          id: path.id || "",
          name: path.name || "",
          steps: Array.isArray(path.steps) 
            ? path.steps.map((s: any) => transformDbActivityToTimeline(s))
            : [],
        }))
      : [];
  }

  // Handle parallel groups
  if (dbActivity.is_parallel_group && dbActivity.parallel_activities) {
    step.isParallelGroup = true;
    step.parallelActivities = Array.isArray(dbActivity.parallel_activities)
      ? dbActivity.parallel_activities.map((activity: any) => {
          const transformedActivity = transformDbActivityToTimeline(activity);
          // Inherit patient_id from parent if missing
          if (!transformedActivity.patientId && dbActivity.patient_id) {
            transformedActivity.patientId = dbActivity.patient_id;
          }
          return transformedActivity;
        })
      : [];
    step.completedCount = dbActivity.completed_count || 0;
    step.totalCount = dbActivity.total_count || step.parallelActivities?.length || 0;
  }

  // Handle merge points
  if (dbActivity.is_merge_point) {
    step.isMergePoint = true;
  }

  return step;
};
